import { useEffect, useRef, useState } from 'react';
import type { IconCategory, MapMode } from '@warplan/shared';
import { ICON_CATALOG, getOrderedCategoriesForMode } from '../lib/icon-catalog.js';
import { IconPalette } from './IconPalette.js';

export interface MapContextMenuProps {
  /** Screen-space anchor (client coordinates). */
  x: number;
  y: number;
  canEdit: boolean;
  onPick: (category: IconCategory, iconId: string) => void;
  onClose: () => void;
  /** Active map mode — selects which category list to show. */
  mode?: MapMode;
  /** True if the current user can toggle the hexagon disabled state (admin). */
  canToggleHex?: boolean;
  /** True if the right-clicked hexagon is currently disabled. */
  hexIsDisabled?: boolean;
  /** Called when the user toggles the hexagon disabled state. */
  onToggleHex?: () => void;
  /** Categories that are entirely off-limits for the current user. */
  forbiddenCategories?: ReadonlySet<IconCategory> | undefined;
  /** Icon IDs that are off-limits in the submenus (greyed out). */
  forbiddenIconIds?: ReadonlySet<string> | undefined;
}

const MENU_W = 180;
const SUBMENU_W = 196; // 4 cols × ~44px + padding

export function MapContextMenu({
  x,
  y,
  canEdit,
  onPick,
  onClose,
  mode = 'planner',
  canToggleHex = false,
  hexIsDisabled = false,
  onToggleHex,
  forbiddenCategories,
  forbiddenIconIds,
}: MapContextMenuProps): React.JSX.Element {
  const orderedCategories = getOrderedCategoriesForMode(mode);
  const [open, setOpen] = useState<IconCategory | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Clamp main menu inside viewport.
  const vpW = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vpH = typeof window !== 'undefined' ? window.innerHeight : 768;
  const showCategories = !hexIsDisabled;
  const toggleRowH = canToggleHex ? 32 : 0;
  const categoriesH = showCategories ? orderedCategories.length * 32 : 0;
  const menuH = toggleRowH + categoriesH + 8;
  const left = Math.min(x, vpW - MENU_W - 8);
  const top = Math.min(y, vpH - menuH - 8);

  // Decide submenu side (right by default, left if overflow).
  const submenuOnLeft = left + MENU_W + SUBMENU_W + 8 > vpW;

  return (
    <div
      ref={rootRef}
      role="menu"
      className="fixed z-40 rounded border border-slate-700 bg-slate-900/95 shadow-xl backdrop-blur"
      style={{ left, top, width: MENU_W }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <ul className="py-1 text-sm text-slate-100">
        {canToggleHex && onToggleHex && (
          <li
            role="menuitem"
            tabIndex={0}
            onClick={() => {
              onToggleHex();
              onClose();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onToggleHex();
                onClose();
              }
            }}
            className={
              'flex items-center gap-2 px-3 py-1.5 cursor-pointer ' +
              (hexIsDisabled
                ? 'text-emerald-300 hover:bg-emerald-900/40'
                : 'text-amber-300 hover:bg-amber-900/30') +
              (showCategories ? ' border-b border-slate-700' : '')
            }
          >
            <span aria-hidden="true">{hexIsDisabled ? '✓' : '⊘'}</span>
            <span>{hexIsDisabled ? 'Réactiver cet hexagone' : 'Désactiver cet hexagone'}</span>
          </li>
        )}
        {showCategories && orderedCategories.map((cat) => {
          const meta = ICON_CATALOG[cat];
          const disabled = !canEdit || (forbiddenCategories?.has(cat) ?? false);
          const isOpen = open === cat;
          return (
            <li
              key={cat}
              role="menuitem"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              aria-haspopup="true"
              aria-expanded={isOpen}
              onMouseEnter={() => !disabled && setOpen(cat)}
              onFocus={() => !disabled && setOpen(cat)}
              className={
                'flex items-center justify-between px-3 py-1.5 ' +
                (disabled
                  ? 'opacity-40 cursor-not-allowed'
                  : isOpen
                    ? 'bg-slate-700/70'
                    : 'hover:bg-slate-700/70 cursor-pointer')
              }
            >
              <span>{meta.label}</span>
              <span aria-hidden="true">▸</span>
              {isOpen && !disabled && (
                <div
                  className="absolute top-0"
                  style={
                    submenuOnLeft ? { right: MENU_W - 2 } : { left: MENU_W - 2 }
                  }
                >
                  <IconPalette
                    category={cat}
                    onPick={(iconId) => {
                      onPick(cat, iconId);
                      onClose();
                    }}
                    variant="submenu"
                    forbiddenIconIds={forbiddenIconIds}
                  />
                </div>
              )}
            </li>
          );
        })}
        {showCategories && !canEdit && (
          <li className="border-t border-slate-700 px-3 py-1.5 text-xs text-amber-300">
            Editor or admin role required to place markers.
          </li>
        )}
      </ul>
    </div>
  );
}
